import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Meta, MetaDefinition, Title } from "@angular/platform-browser";
import { BaseService } from "@core/services/base.service";
import { DeviceService } from "@core/services/device.service";
import { LoadingService } from "@core/services/loading.service";
import { TitleServiceInterface } from "@core/services/title/title.service-interface";

@Injectable({
  providedIn: "root"
})
export class TitleService extends BaseService implements TitleServiceInterface {
  constructor(
    public readonly loadingService: LoadingService,
    public readonly titleService: Title,
    public readonly meta: Meta,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly deviceService: DeviceService
  ) {
    super(loadingService);
  }

  public getTitle(): string {
    return this.titleService.getTitle();
  }

  public setTitle(title: string) {
    const suffix = " - AstroBin";
    if (title.endsWith(suffix)) {
      title = title.replace(suffix, "");
    }

    this.titleService.setTitle(title + suffix);
    this.updateMetaTag({ property: "og:title", content: title + suffix });
  }

  public updateMetaTag(tag: MetaDefinition) {
    this._addOrUpdateMetaTag(tag);
  }

  public addMetaTag(tag: MetaDefinition) {
    this._addOrUpdateMetaTag(tag);
  }

  public getDescription(): string {
    return this.meta.getTag("name='description'")?.content;
  }

  public setDescription(description: string) {
    this.updateMetaTag({ name: "description", content: description });
    this.updateMetaTag({ property: "og:description", content: description });
  }

  // Disables native browser gestures for custom touch controls (used by fullscreen image viewer)
  public disableNativeGestures() {
    // Update viewport meta tag to prevent zooming
    this.updateMetaTag({
      name: "viewport",
      content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
    });

    if (isPlatformBrowser(this.platformId)) {
      // Prevent multi-touch gestures (pinch to zoom)
      document.addEventListener("touchmove", this._preventDefault, { passive: false });

      // Disable all touch actions to allow custom gesture handling
      const htmlElement = document.querySelector("html");
      if (htmlElement) {
        htmlElement.style.setProperty("touch-action", "none");
      }

      // Firefox touchpad pinch to zoom can also happen with ctrl+wheel events
      this._wheelHandler = (e: WheelEvent) => {
        if (e.ctrlKey) {
          e.preventDefault();
        }
      };
      document.addEventListener("wheel", this._wheelHandler, { passive: false });
    }
  }

  // Disables zoom for PWA mode while preserving scrolling
  public disablePwaZoom() {
    // Update viewport meta tag to prevent zooming
    this.updateMetaTag({
      name: "viewport",
      content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
    });
    // Note: CSS touch-action is handled via .pwa-mode class in _common.scss
  }

  // Store references to event handlers so we can properly remove them
  private _wheelHandler: ((e: WheelEvent) => void) | null = null;

  // Re-enables native browser gestures after custom controls (used by fullscreen image viewer)
  public enableNativeGestures() {
    // Check if we're in PWA mode before resetting viewport
    const isPwa = this.deviceService?.isPwa?.() || false;

    // Reset viewport meta tag - but preserve PWA no-zoom settings
    this.updateMetaTag({
      name: "viewport",
      content: isPwa
        ? "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        : "width=device-width, initial-scale=1"
    });

    if (isPlatformBrowser(this.platformId)) {
      // Remove touch event listener
      document.removeEventListener("touchmove", this._preventDefault);

      // Remove wheel event listener if it exists
      if (this._wheelHandler) {
        document.removeEventListener("wheel", this._wheelHandler);
        this._wheelHandler = null;
      }

      // Reset touch-action on html element
      const htmlElement = document.querySelector("html");
      if (htmlElement) {
        htmlElement.style.removeProperty("touch-action");
      }
    }
  }

  private _getExistingMetaTag(tag: MetaDefinition) {
    if (tag.name) {
      return this.meta.getTag(`name="${tag.name}"`);
    }

    if (tag.property) {
      return this.meta.getTag(`property="${tag.property}"`);
    }

    return null;
  }

  private _addOrUpdateMetaTag(tag: MetaDefinition) {
    const existingTag = this._getExistingMetaTag(tag);

    if (existingTag) {
      this.meta.updateTag(tag);
    } else {
      this.meta.addTag(tag);
    }
  }

  private _preventDefault(e: TouchEvent) {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  }
}
